import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useMedicalClearances,
  MedicalClearance,
  PROVIDER_TYPE_LABELS,
} from "@/hooks/use-medical-clearances";
import { AddClearanceDialog } from "./AddClearanceDialog";
import { format, isPast, addDays, formatDistanceToNow } from "date-fns";
import {
  Stethoscope,
  Plus,
  MoreVertical,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Trash2,
  FileText,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface MedicalClearanceCardProps {
  clientId: string;
}

export function MedicalClearanceCard({ clientId }: MedicalClearanceCardProps) {
  const {
    clearances,
    loading,
    error,
    creating,
    addClearance,
    verifyClearance,
    deleteClearance,
    hasActiveClearance,
  } = useMedicalClearances(clientId);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingClearance, setDeletingClearance] = useState<MedicalClearance | null>(null);

  const getClearanceStatus = (clearance: MedicalClearance) => {
    if (clearance.expires_at) {
      if (isPast(new Date(clearance.expires_at))) {
        return { label: "Expired", color: "destructive", icon: AlertTriangle };
      }
      const thirtyDaysFromNow = addDays(new Date(), 30);
      if (new Date(clearance.expires_at) <= thirtyDaysFromNow) {
        return { label: "Expiring Soon", color: "warning", icon: Clock };
      }
    }
    if (clearance.verified_at) {
      return { label: "Verified", color: "success", icon: ShieldCheck };
    }
    return { label: "Active", color: "success", icon: CheckCircle2 };
  };

  const handleDelete = async () => {
    if (deletingClearance) {
      await deleteClearance(deletingClearance.id);
      setDeleteConfirmOpen(false);
      setDeletingClearance(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">{error}</CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Stethoscope className="h-5 w-5" />
                Medical Clearances
              </CardTitle>
              <CardDescription>
                {hasActiveClearance()
                  ? "Client has active medical clearance"
                  : "No active clearances on file"}
              </CardDescription>
            </div>
            <Button onClick={() => setAddDialogOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add Clearance
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {clearances.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Stethoscope className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No medical clearances on file</p>
              <p className="text-sm mt-1">
                Add a clearance when the client provides documentation
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {clearances.map((clearance) => {
                const status = getClearanceStatus(clearance);
                const StatusIcon = status.icon;

                return (
                  <div
                    key={clearance.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={cn(
                          "p-2 rounded-full",
                          status.color === "success" && "bg-success/15",
                          status.color === "warning" && "bg-warning/15",
                          status.color === "destructive" && "bg-destructive/15"
                        )}
                      >
                        <StatusIcon
                          className={cn(
                            "h-4 w-4",
                            status.color === "success" && "text-success",
                            status.color === "warning" && "text-warning",
                            status.color === "destructive" && "text-destructive"
                          )}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{clearance.provider_name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{PROVIDER_TYPE_LABELS[clearance.provider_type]}</span>
                          <span>•</span>
                          <span>
                            Cleared {format(new Date(clearance.clearance_date), "MMM d, yyyy")}
                          </span>
                          {clearance.expires_at && (
                            <>
                              <span>•</span>
                              <span>
                                {isPast(new Date(clearance.expires_at))
                                  ? `Expired ${formatDistanceToNow(new Date(clearance.expires_at), { addSuffix: true })}`
                                  : `Expires ${formatDistanceToNow(new Date(clearance.expires_at), { addSuffix: true })}`}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          status.color === "success"
                            ? "default"
                            : status.color === "warning"
                            ? "secondary"
                            : "destructive"
                        }
                        className={cn(
                          status.color === "success" &&
                            "bg-success/15 text-success hover:bg-success/15"
                        )}
                      >
                        {status.label}
                      </Badge>
                      {clearance.document_path && (
                        <Button variant="ghost" size="sm" asChild>
                          <a
                            href={clearance.document_path}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FileText className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!clearance.verified_at && (
                            <DropdownMenuItem onClick={() => verifyClearance(clearance.id)}>
                              <ShieldCheck className="h-4 w-4 mr-2" />
                              Mark as Verified
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setDeletingClearance(clearance);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Clearance Dialog */}
      <AddClearanceDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        clientId={clientId}
        onAdd={addClearance}
        adding={creating}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Medical Clearance?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this clearance from{" "}
              {deletingClearance?.provider_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
